// @flow

import * as React from 'react';
import { Closer, Opener } from '@ta/overlays.managers';
import HoverChain from '@ta/common.hover-chain';
import type { HoverChainNode } from '@ta/common.hover-chain';
import MenuTab from './MenuTab';
import type { TabType, NavLinkType } from '../types';

type Props = {|
  /** Name of the overlay to trigger */
  name: string,

  /** Whether or not the trigger should be in an active state (e.g., indicate we're currently on /A) */
  triggerActive: boolean,

  /** The function that renders item text */
  itemText: ?(() => void, () => void, () => void, () => void) => React.Node,

  menuStateCallback: ?(boolean) => void,

  /** Function that takes render props. */
  children: (HTMLElement, HoverChainNode) => React.Node,

  /** Whether the tab is a link or a non-link */
  tabType?: TabType,

  /** Data model for the link, if TabType === link */
  link?: NavLinkType,

  /** Data model for the tab contents, if TabType === nonlink, e.g. for the ellipsis menu which is a span */
  nonLinkContents?: React.Node,

  /**  Pointer to a parent node. Optional */
  hoverChainParent?: HoverChainNode,
|};

type State = {|
  hovering: boolean,
|};

const mouseOutTimeout = 0;

class MenuTrigger extends React.Component<Props, State> {
  static defaultProps = {
    itemText: null,
  };

  constructor(props: Props) {
    super(props);
    this.triggerWrapRef = null;

    this.state = {
      hovering: false,
    };
  }

  mouseOverHandler = () => {
    if (this.props.menuStateCallback) {
      this.props.menuStateCallback(true);
    }
    this.setState({ hovering: true });
  };

  mouseOutHandler = () => {
    if (this.props.menuStateCallback) {
      this.props.menuStateCallback(false);
    }
    this.setState({ hovering: false });
  };

  triggerWrapRef: any;

  renderMenuTab = (setTarget: () => void, setWrapper: () => void) => (
    <div ref={setWrapper}>
      <MenuTab
        setHoverTarget={setTarget}
        hovering={this.state.hovering}
        active={this.props.triggerActive}
        tabType={this.props.tabType}
        link={this.props.link}
        nonLinkContents={this.props.nonLinkContents}
      />
    </div>
  );

  render() {
    return (
      <Opener name={this.props.name} local>
        {open => (
          <Closer name={this.props.name}>
            {close => (
              <HoverChain
                hoverChainParent={this.props.hoverChainParent}
                mouseOutTimeout={mouseOutTimeout}
                mouseOverHandler={() => {
                  open();
                  this.mouseOverHandler();
                }}
                mouseOutHandler={() => {
                  close();
                  this.mouseOutHandler();
                }}
              >
                {(setTarget, hoverChainParent) => (
                  <React.Fragment>
                    {this.props.itemText
                      ? this.props.itemText(
                          setTarget,
                          el => {
                            this.triggerWrapRef = el;
                          },
                          open,
                          close,
                        )
                      : this.renderMenuTab(setTarget, el => {
                          this.triggerWrapRef = el;
                        })}
                    {this.props.children(this.triggerWrapRef, hoverChainParent)}
                  </React.Fragment>
                )}
              </HoverChain>
            )}
          </Closer>
        )}
      </Opener>
    );
  }
}

export default MenuTrigger;
